import type {
  PickemEventDetail,
  PickemUserBracket,
  PickemUserPick,
  SubmitPickemPicksData,
} from "@api/generated/types.gen";

export const draftPreviewEnabled =
  import.meta.env.DEV &&
  import.meta.env.MODE === "draft-preview" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);
const key = (id: string) => `local-draft-pickem:${id}`;
async function drafts(): Promise<PickemEventDetail[]> {
  const response = await fetch("/__local-pickem-drafts");
  if (!response.ok)
    throw new Error(
      "Draft snapshot is unavailable. Run the local snapshot export after AWS login.",
    );
  // This snapshot is generated by our local export script from the read-only database query.
  return response.json();
}
export async function previewEvents() {
  return (await drafts()).map((d) => d.event);
}
export async function previewBracket(id: string): Promise<PickemUserBracket> {
  const detail = (await drafts()).find((d) => d.event.id === id);
  if (!detail) throw new Error("Draft event not present in the local snapshot");
  const saved: SubmitPickemPicksData["body"] = JSON.parse(
    localStorage.getItem(key(id)) ?? '{"picks":[]}',
  );
  return {
    ...detail,
    userPicks: previewPicks(id, saved),
    userLineup: saved?.lineup ?? [],
    score: {
      userId: "local-preview",
      totalPoints: 0,
      correctPicks: 0,
      totalPicks: saved?.picks.length ?? 0,
    },
  };
}
function previewPicks(
  id: string,
  body: SubmitPickemPicksData["body"],
): PickemUserPick[] {
  return (body?.picks ?? []).map((p) => ({
    ...p,
    id: `preview-${p.bracketMatchId}`,
    eventId: id,
    userId: "local-preview",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    platform: "web",
    predictedLoserScore: p.predictedLoserScore ?? null,
    predictedReset: p.predictedReset ?? null,
  }));
}
export async function savePreviewPicks(
  input: Pick<SubmitPickemPicksData, "path" | "body">,
): Promise<PickemUserPick[]> {
  if (!draftPreviewEnabled) throw new Error("Local preview is disabled");
  const previous = JSON.parse(
    localStorage.getItem(key(input.path.eventId)) ?? "{}",
  );
  const saved = { ...previous, ...input.body };
  localStorage.setItem(key(input.path.eventId), JSON.stringify(saved));
  return previewPicks(input.path.eventId, saved);
}
